import {
  useEffect,
  useCallback,
  Dispatch,
  SetStateAction,
  useMemo,
  useState,
  useRef,
} from "react";
import { Button } from "../../../../components/ui/button";
import { X, LockOpen, Lock as LockIcon } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../../components/ui/tooltip";
import { Carousel } from "../../../../components/ui/carousel";
import FileViewer, {
  BoundingBox,
} from "../../../extraction/viewExtraction/FileViewer";
import { LinkingData } from "../../../extraction/viewExtraction/PdfViewer";
import { pdfDocument, VideoDocument } from "../../../../models/extraction";
import { z } from "zod";
import { Input } from "../../../../components/ui/input";
import { debounce } from "lodash";
import { fetchFastApi } from "../../../../api/fastapi";
import { toast } from "sonner";
import { useAudit } from "../../../../contexts/auditContext";
import { useProcedureSessionDocuments } from "../../../../hooks/audit/useProcedureSessionDocuments";
import {
  FileType,
  searchResultSchema,
} from "../../../../models/audit/auditSession";

type CurrentSelectionType = {
  checkResultId?: string;
  sourceRowId?: string;
  documentId?: string;
  attributeId?: string;
  supportColumnId?: string;
  value?: string;
};

type ViewerBox = {
  id?: string;
  boundingBox?: BoundingBox;
  elementId: string;
  pageWidth: number;
  pageHeight: number;
  pageNumber: number;
  shortSummary?: string;
  documentId?: string;
  timestampSeconds?: number;
};

interface SupportColSidePanelV2Props {
  selectedDocument: {
    fileName?: string;
    fetchableDocument?: { fileName?: string; fileType?: string };
    fileType?: string;
  } | null;
  currentSelectedDocumentUrl?: {
    url: string;
    documentExtraction?: z.infer<typeof pdfDocument>;
    videoDocument?: VideoDocument;
  } | null;
  currentSelection?: CurrentSelectionType;
  currentBoundingBoxes?: ViewerBox[];
  selectedBoundingIndex: number;
  currentPage: number;
  setSelectedBoundingIndex: Dispatch<SetStateAction<number>>;
  setCurrentPage: (page: number) => void;
  onClose: () => void;
  onLinkingComplete: (data: LinkingData) => Promise<void> | void;
  onDelete: ({
    id,
    index,
  }: {
    id?: string;
    index: number;
  }) => Promise<void> | void;
  currentAuditSessionId?: string;
  onSelectNewDocument?: (
    doc: {
      id: string;
      fileName?: string | null;
      fileType?: string;
    },
    opts?: { pageNumber?: number },
  ) => void;
  lockedBy?: string | null;
  onToggleLock?: () => Promise<void> | void;
}

export const SupportColSidePanelV2 = ({
  selectedDocument,
  currentSelectedDocumentUrl,
  currentSelection,
  currentBoundingBoxes,
  selectedBoundingIndex,
  currentPage,
  setSelectedBoundingIndex,
  setCurrentPage,
  onClose,
  onLinkingComplete,
  onDelete,
  currentAuditSessionId,
  onSelectNewDocument,
  lockedBy,
  onToggleLock,
}: SupportColSidePanelV2Props) => {
  const { controlSession } = useAudit();
  // Phase 3 wiring: documents come from the per-procedure documents slice
  // (Phase 2) which is keyed under `['procedure-session', psId, 'documents']`
  // and stays consistent with the table-shell entry via the shared
  // `['procedure-session', psId]` prefix invalidations from
  // `useControlSessionStatus`.
  const { data: procedureSessionDocuments } = useProcedureSessionDocuments(
    currentAuditSessionId,
  );
  const filteredBoxes = useMemo(
    () =>
      (currentBoundingBoxes || []).filter((bb) =>
        bb.documentId && currentSelection?.documentId
          ? bb.documentId === currentSelection.documentId
          : true,
      ),
    [currentBoundingBoxes, currentSelection?.documentId],
  );
  const hasBoxes = (currentBoundingBoxes?.length || 0) > 0;
  const selectedFilteredIndex = useMemo(() => {
    const currentSelectedId = currentBoundingBoxes?.[selectedBoundingIndex]?.id;
    const idx = filteredBoxes.findIndex((b) => b.id === currentSelectedId);
    return idx >= 0 ? idx : null;
  }, [filteredBoxes, currentBoundingBoxes, selectedBoundingIndex]);

  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<
    Array<{
      documentId: string;
      fileName: string | null;
      fileType: string;
      highlight?: string;
    }>
  >([]);
  const [showResults, setShowResults] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement | null>(null);

  const allDocumentsForSession = useMemo(() => {
    if (!currentAuditSessionId)
      return [] as Array<{
        documentId: string;
        fileName: string | null;
        fileType: string;
        highlight?: string;
      }>;
    const docs = procedureSessionDocuments?.documents ?? [];
    return docs.map((doc) => ({
      documentId: doc.documentId, // uploaded document id used for selection
      fileName: doc.fetchableDocument?.fileName || null,
      fileType: (doc.fetchableDocument?.fileType as string) || "pdf",
      highlight: "",
    }));
  }, [procedureSessionDocuments, currentAuditSessionId]);

  const debouncedSearch = useMemo(
    () =>
      debounce(async (term: string) => {
        if (!term || !currentAuditSessionId) {
          setSearchResults([]);
          return;
        }
        try {
          setIsSearching(true);
          const response = await fetchFastApi({
            method: "GET",
            path: `/matching/match-session/${currentAuditSessionId}/documents/search`,
            query: { query: term },
          });

          const results: Array<{
            documentId: string;
            fileName: string | null;
            fileType: string;
            highlight?: string;
          }> = [];
          (response.documents || []).forEach(
            (doc: z.infer<typeof searchResultSchema>) => {
              results.push({
                documentId: doc.id,
                fileName: doc.fileName || null,
                fileType: doc.fileType || "pdf",
                highlight: doc.highlight || "",
              });
            },
          );
          setSearchResults(results);
        } catch (error) {
          console.error("Error searching documents:", error);
          toast.error("Failed to search documents");
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      }, 300),
    [currentAuditSessionId],
  );

  useEffect(() => {
    if (!showResults) return;
    if (!searchTerm || searchTerm.trim() === "") {
      setSearchResults(allDocumentsForSession);
    } else {
      debouncedSearch(searchTerm);
    }
    return () => debouncedSearch.cancel();
  }, [searchTerm, showResults, debouncedSearch, allDocumentsForSession]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      if (!searchContainerRef.current) return;
      if (!searchContainerRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const handleCarouselSelect = useCallback(
    (index: number) => {
      const box = filteredBoxes[index];
      if (!box) return;
      const globalIndex = (currentBoundingBoxes || []).findIndex(
        (bb) => bb.id === box.id,
      );
      if (globalIndex >= 0) setSelectedBoundingIndex(globalIndex);
      const nextPage = box.pageNumber || 1;
      setCurrentPage(nextPage);
      const nextDocId = box.documentId;
      if (nextDocId) {
        onSelectNewDocument?.({ id: nextDocId }, { pageNumber: nextPage });
      }
    },
    [
      filteredBoxes,
      currentBoundingBoxes,
      setSelectedBoundingIndex,
      setCurrentPage,
      onSelectNewDocument,
    ],
  );

  const handleCarouselSelectGlobal = useCallback(
    (index: number) => {
      setSelectedBoundingIndex(index);
      const nextPage = currentBoundingBoxes?.[index]?.pageNumber || 1;
      setCurrentPage(nextPage);
      const nextDocId = currentBoundingBoxes?.[index]?.documentId;
      if (nextDocId) {
        onSelectNewDocument?.({ id: nextDocId }, { pageNumber: nextPage });
      }
    },
    [
      currentBoundingBoxes,
      setSelectedBoundingIndex,
      setCurrentPage,
      onSelectNewDocument,
    ],
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tagName = target?.tagName?.toLowerCase();
      if (
        tagName === "input" ||
        tagName === "textarea" ||
        target?.isContentEditable
      ) {
        return;
      }
      if (!currentBoundingBoxes || currentBoundingBoxes.length === 0) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        const prevIndex =
          selectedBoundingIndex <= 0
            ? currentBoundingBoxes.length - 1
            : selectedBoundingIndex - 1;
        const prevBox = currentBoundingBoxes?.[prevIndex];
        if (prevBox?.documentId) {
          onSelectNewDocument?.(
            { id: prevBox.documentId },
            { pageNumber: prevBox.pageNumber || 1 },
          );
        }
        setSelectedBoundingIndex(prevIndex);
        setCurrentPage(prevBox?.pageNumber || 1);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        const nextIndex =
          selectedBoundingIndex >= currentBoundingBoxes.length - 1
            ? 0
            : selectedBoundingIndex + 1;
        const nextBox = currentBoundingBoxes?.[nextIndex];
        if (nextBox?.documentId) {
          onSelectNewDocument?.(
            { id: nextBox.documentId },
            { pageNumber: nextBox.pageNumber || 1 },
          );
        }
        setSelectedBoundingIndex(nextIndex);
        setCurrentPage(nextBox?.pageNumber || 1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    currentBoundingBoxes,
    selectedBoundingIndex,
    handleCarouselSelect,
    onSelectNewDocument,
    setSelectedBoundingIndex,
    setCurrentPage,
  ]);

  return (
    <div className="h-screen overflow-hidden flex flex-row">
      {currentSelection?.documentId && currentSelectedDocumentUrl && (
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="py-2 px-4 flex justify-between items-center shrink-0">
            <img src="/logo_orange_bg.svg" alt="logo" className="w-8" />
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-6 w-6" />
            </Button>
          </div>
          <div ref={searchContainerRef} className="px-4 pb-2 relative">
            <Input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                if (!showResults) setShowResults(true);
              }}
              onFocus={() => {
                setShowResults(true);
                if (!searchTerm || searchTerm.trim() === "") {
                  setSearchResults(allDocumentsForSession);
                }
              }}
              onBlur={() => {
                // allow outside click handler to determine visibility
                // keep no-op here to avoid immediate close when clicking dropdown
              }}
              placeholder="Search documents to link citations..."
              className="w-full"
            />
            {showResults && (
              <div className="absolute mt-1 z-20 max-h-64 w-[calc(100%-2rem)] overflow-y-auto bg-white border border-stone-200 rounded shadow-sm">
                {isSearching && (
                  <div className="p-2 text-sm text-stone-500">Searching...</div>
                )}
                {!isSearching && searchResults.length === 0 && (
                  <div className="p-2 text-sm text-stone-500">No results</div>
                )}
                {!isSearching &&
                  searchResults.map((r) => (
                    <button
                      key={r.documentId}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-stone-50"
                      onClick={() => {
                        onSelectNewDocument?.({
                          id: r.documentId,
                          fileName: r.fileName,
                          fileType: r.fileType,
                        });
                        setShowResults(false);
                        setSearchTerm("");
                        setSearchResults([]);
                      }}
                    >
                      <div className="font-medium truncate">
                        {r.fileName || "Untitled Document"}
                      </div>
                    </button>
                  ))}
              </div>
            )}
          </div>
          <div className="ml-4 mb-1 flex items-center gap-2">
            <div className="w-fit max-w-60 px-4 py-2 text-sm bg-stone-50 border border-stone-200 truncate shrink-0 rounded">
              {selectedDocument?.fileName ||
                selectedDocument?.fetchableDocument?.fileName ||
                ""}
            </div>
            {hasBoxes && (
              <Carousel
                items={
                  currentBoundingBoxes?.map((_, idx) => ({
                    id: idx,
                    content: idx + 1,
                  })) || []
                }
                selectedIndex={
                  currentBoundingBoxes?.[selectedBoundingIndex]?.documentId ===
                  currentSelection?.documentId
                    ? selectedBoundingIndex
                    : -1
                }
                onItemSelect={handleCarouselSelectGlobal}
                className="mr-4"
              />
            )}
          </div>
          <div className="px-4 pb-2 pt-1">
            <div className="text-sm" data-testid="workpaper-citation-summary">
              {currentBoundingBoxes?.[selectedBoundingIndex]?.shortSummary}
            </div>
          </div>
          <div className="flex-1 min-h-0">
            <div className="h-full overflow-hidden">
              <FileViewer
                allowLinking={controlSession?.status !== "complete"}
                allowDeleting={controlSession?.status !== "complete"}
                signedUrl={currentSelectedDocumentUrl?.url}
                fileType={(selectedDocument?.fileType || "pdf") as FileType}
                initialPage={currentPage}
                boundingBoxes={filteredBoxes.flatMap((bb) =>
                  bb.boundingBox
                    ? [
                        {
                          id: bb.id,
                          boundingBox: bb.boundingBox,
                          pageNumber: bb.pageNumber,
                          pageWidth: bb.pageWidth,
                          pageHeight: bb.pageHeight,
                        },
                      ]
                    : [],
                )}
                boundingBox={
                  (selectedDocument?.fileType || "pdf").toLowerCase() !== "pdf"
                    ? selectedFilteredIndex !== null
                      ? filteredBoxes?.[selectedFilteredIndex]?.boundingBox
                      : undefined
                    : undefined
                }
                pageWidth={
                  (selectedDocument?.fileType || "pdf").toLowerCase() !== "pdf"
                    ? selectedFilteredIndex !== null
                      ? filteredBoxes?.[selectedFilteredIndex]?.pageWidth
                      : undefined
                    : undefined
                }
                pageHeight={
                  (selectedDocument?.fileType || "pdf").toLowerCase() !== "pdf"
                    ? selectedFilteredIndex !== null
                      ? filteredBoxes?.[selectedFilteredIndex]?.pageHeight
                      : undefined
                    : undefined
                }
                elementId={
                  selectedFilteredIndex !== null
                    ? filteredBoxes?.[selectedFilteredIndex]?.elementId
                    : undefined
                }
                selectedBoundingBoxId={
                  selectedFilteredIndex !== null
                    ? filteredBoxes?.[selectedFilteredIndex]?.id
                    : undefined
                }
                onSelect={({ index }) => {
                  handleCarouselSelect(index);
                }}
                fileName={
                  selectedDocument?.fileName ||
                  selectedDocument?.fetchableDocument?.fileName
                }
                defaultSearchValue={currentSelection?.value}
                documentExtraction={
                  currentSelectedDocumentUrl?.documentExtraction
                }
                videoDocument={currentSelectedDocumentUrl?.videoDocument}
                initialTimestamp={(() => {
                  const ts =
                    selectedFilteredIndex !== null
                      ? filteredBoxes?.[selectedFilteredIndex]?.timestampSeconds
                      : undefined;
                  console.log(
                    "[SupportColSidePanelV2] Passing initialTimestamp to FileViewer:",
                    ts,
                    "selectedFilteredIndex:",
                    selectedFilteredIndex,
                    "filteredBox:",
                    selectedFilteredIndex !== null
                      ? filteredBoxes?.[selectedFilteredIndex]
                      : null,
                  );
                  return ts;
                })()}
                onLinkingComplete={onLinkingComplete}
                onDelete={onDelete}
              />
            </div>
          </div>
        </div>
      )}
      {currentSelection?.documentId && currentSelectedDocumentUrl && (
        <div className="border-l border-gray-200 flex items-center justify-center p-2 shrink-0">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={onToggleLock}
                  disabled={!onToggleLock}
                  className={`${lockedBy ? "text-white bg-green-700 hover:bg-green-800 hover:text-white" : "text-green-700 bg-gray-100 hover:bg-gray-200 hover:text-green-700"}`}
                >
                  {lockedBy ? (
                    <LockOpen className="h-4 w-4" />
                  ) : (
                    <LockIcon className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{lockedBy ? "Unlock Cell" : "Lock Cell"}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}
    </div>
  );
};
